/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from 'app/lib/utils';
import { MenuItem } from 'app/types/navigation';
import { appRoutes } from 'app/routes';
import { menuConfig } from '../dashboard-header';
import { matchPath } from './nav-item';
import { useBreadcrumb } from './breadcrumb-provider';

/**
 * Resolve the deepest item matching the current path.
 */

function longestMatch<T extends { pathname?: string }>(pathname: string, items: T[]): T | undefined {
  return [...items]
    .filter(item => matchPath(pathname, item.pathname))
    .sort((a, b) => (b.pathname?.length ?? 0) - (a.pathname?.length ?? 0))[0];
}

/**
 * Export `Breadcrumb` component.
 */

export function Breadcrumb({ menu }: { menu: MenuItem[] }): JSX.Element | null {
  const [translate] = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { trail } = useBreadcrumb();
  const isHome = pathname === appRoutes.dashboard.home.base || pathname === appRoutes.dashboard.base;
  const product = useMemo(() => longestMatch(pathname, menuConfig), [pathname]);
  const page = useMemo(() => longestMatch(pathname, menu), [menu, pathname]);
  const productLabel = product ? translate(product.label as string) : '';
  const pageLabel = page?.label ? translate(page.label as string) : '';

  // Build the trail: product > section > record. The section crumb is kept even
  // when it repeats the product name (e.g. "Bank Accounts > Bank Accounts"),
  // and the current record name published by the page is appended (e.g. "… >
  // Conta Refeição"). A page can also publish an explicit list of crumbs that
  // *replaces* the auto section crumb, to preserve the navigation it was reached
  // through (e.g. an asset opened from a wallet: "Wallets > My Wallet > BTC").
  const crumbs = useMemo(() => {
    const items: Array<{ label: string; pathname?: string }> = [];

    if (productLabel) {
      items.push({ label: productLabel, pathname: product?.pathname });
    }

    if (trail?.replacesSection) {
      trail.crumbs.forEach(crumb => items.push(crumb));

      return items;
    }

    if (pageLabel && menu.length > 1) {
      items.push({ label: pageLabel, pathname: page?.pathname });
    }

    const record = trail?.crumbs[0]?.label;

    if (record && record !== items[items.length - 1]?.label) {
      items.push({ label: record });
    }

    return items;
  }, [productLabel, pageLabel, menu.length, trail, product?.pathname, page?.pathname]);

  if (isHome || crumbs.length === 0) {
    return null;
  }

  return (
    <nav aria-label={'breadcrumb'} className={'flex items-center gap-1.5 text-sm'}>
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        const clickable = !last && !!crumb.pathname && crumb.pathname !== pathname;

        return (
          <React.Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && <ChevronRight className={'h-3.5 w-3.5 text-muted-foreground/60'} />}

            {clickable ? (
              <button
                className={
                  'p-0 font-normal leading-none text-muted-foreground transition-colors hover:text-foreground hover:underline'
                }
                onClick={() => navigate(crumb.pathname as string)}
                type={'button'}
              >
                {crumb.label}
              </button>
            ) : (
              // Same font-weight as the clickable crumbs so a crumb switching
              // between last/non-last (e.g. "Overview" when you open an asset)
              // doesn't change width and shift the row. The current page is
              // distinguished by colour only.
              <span className={cn('font-normal leading-none', last ? 'text-foreground' : 'text-muted-foreground')}>
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
